import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const formatFieldList = (fields = []) => fields.map((field) => {
  if (field === 'raison_sociale') {
    return 'raison sociale';
  }

  return field;
}).join(', ');

const getAlertKey = (alert, index) => alert?.id || [
  alert?.raison_sociale,
  alert?.nif,
  alert?.stat,
  alert?.conflict_stat,
  index,
].join('|');

const ValidationAlerte = ({
  open,
  alerts = [],
  validations = {},
  onClose,
  onSave,
}) => {
  const [decisions, setDecisions] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextDecisions = {};

    alerts.forEach((alert, index) => {
      const key = getAlertKey(alert, index);
      nextDecisions[key] = validations[key] || {
        decision: 'same',
        newStat: '',
      };
    });

    setDecisions(nextDecisions);
  }, [alerts, open, validations]);

  const errors = useMemo(() => {
    const nextErrors = {};

    alerts.forEach((alert, index) => {
      const key = getAlertKey(alert, index);
      const decision = decisions[key];

      if (decision?.decision !== 'different') {
        return;
      }

      const newStat = String(decision.newStat || '').trim();
      const currentStats = [
        String(alert.stat || '').trim(),
        String(alert.conflict_stat || '').trim(),
      ].filter(Boolean);

      if (!newStat) {
        nextErrors[key] = 'Le nouveau STAT est obligatoire.';
        return;
      }

      if (currentStats.some((stat) => stat.toUpperCase() === newStat.toUpperCase())) {
        nextErrors[key] = 'Le nouveau STAT doit etre different des STAT en conflit.';
      }
    });

    return nextErrors;
  }, [alerts, decisions]);

  const hasErrors = Object.keys(errors).length > 0;

  const updateDecision = (key, value) => {
    setDecisions((previous) => ({
      ...previous,
      [key]: {
        ...previous[key],
        decision: value,
      },
    }));
  };

  const updateStat = (key, value) => {
    setDecisions((previous) => ({
      ...previous,
      [key]: {
        ...previous[key],
        newStat: value,
      },
    }));
  };

  const handleSave = () => {
    if (hasErrors) {
      return;
    }

    if (typeof onSave === 'function') {
      onSave(decisions);
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Validation manuelle des alertes partenaires
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            Si deux champs correspondent parmi raison sociale, NIF et STAT, mais que le STAT est different,
            la ligne doit etre verifiee avant synchronisation.
          </Alert>

          {alerts.length === 0 ? (
            <Alert severity="success">Aucune alerte partenaire a valider.</Alert>
          ) : (
            alerts.map((alert, index) => {
              const key = getAlertKey(alert, index);
              const decision = decisions[key] || { decision: 'same', newStat: '' };

              return (
                <Box
                  key={key}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {alert.raison_sociale || 'Partenaire sans raison sociale'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Champs correspondants : {formatFieldList(alert.matched_fields) || 'Non precise'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">Raison sociale</Typography>
                        <Typography variant="body2">{alert.raison_sociale || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">NIF</Typography>
                        <Typography variant="body2">{alert.nif || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">STAT en conflit</Typography>
                        <Typography variant="body2">
                          {alert.stat || '-'} / {alert.conflict_stat || '-'}
                        </Typography>
                      </Box>
                    </Box>

                    <RadioGroup
                      value={decision.decision}
                      onChange={(event) => updateDecision(key, event.target.value)}
                    >
                      <FormControlLabel
                        value="same"
                        control={<Radio />}
                        label="Il s'agit du meme partenaire"
                      />
                      <FormControlLabel
                        value="different"
                        control={<Radio />}
                        label="Il s'agit de deux partenaires differents"
                      />
                    </RadioGroup>

                    {decision.decision === 'different' && (
                      <TextField
                        label="Nouveau numero STAT"
                        value={decision.newStat}
                        onChange={(event) => updateStat(key, event.target.value)}
                        required
                        fullWidth
                        size="small"
                        error={Boolean(errors[key])}
                        helperText={errors[key] || 'Obligatoire pour eviter les conflits.'}
                      />
                    )}
                  </Stack>
                </Box>
              );
            })
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={hasErrors}>
          Valider
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationAlerte;
